/**
 * Pure utility functions for working with transaction objects.
 *
 * No global state dependencies.
 *
 * Usage:
 *   import { extractCertificateNumber, deriveCertNumber, deriveDescription } from './helpers/transaction-utils.js';
 */

/**
 * Internal: matches the Asplundh-shape "bare cert" pattern — a single token of
 * 1–20 alphanumeric/dash characters with at least one digit. Used to discriminate
 * cert-shaped values from description-shaped values when both can land in tx.notes
 * across loaders.
 */
function isBareCertShape(value) {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	return /^(?=.*\d)[A-Z0-9-]{1,20}$/i.test(trimmed);
}

/**
 * Extracts a certificate number from a free-text notes string.
 * Handles formats: #CLASS.NUMBER (e.g., #A.123) or #NUMBER (e.g., #123).
 * For CLASS.NUMBER format, returns only the number portion after the period.
 *
 * @param {string} notes - Free-text notes string (typically tx.notes or tx.description).
 * @returns {string} The extracted certificate identifier, or '' when no match.
 */
export function extractCertificateNumber(notes) {
	if (!notes) return '';
	let certMatch = notes.match(/#[A-Z]+\.([A-Z0-9-]+)/i);
	if (certMatch) return certMatch[1];
	certMatch = notes.match(/#([A-Z0-9-]+)/i);
	return certMatch ? certMatch[1] : '';
}

/**
 * Derives the canonical certificate number for a transaction object across
 * loader shapes. Resolution order:
 *   1. Explicit `tx.certNumber` (set by loaders that read a dedicated cert column).
 *   2. Regex extraction from `tx.notes` (handles `#A.123` / `#123` patterns
 *      embedded in standard CSV notes).
 *   3. Regex extraction from `tx.description`.
 *   4. Bare-cert fallback: when `tx.notes` is a single token of digits, hyphens,
 *      and uppercase letters that contains at least one digit, treat the whole
 *      value as the cert (Asplundh-shape data ships pure-numeric cert strings
 *      in `tx.notes` with no `#` prefix). The digit requirement excludes
 *      operational tokens like `TBD`, `PAID`, `COMMENT`.
 *
 * The single source of truth for both the runtime engine and CSV loaders so
 * `txCertNumber` is identical across paths.
 *
 * @param {Object} tx - Transaction object (any loader shape).
 * @returns {string} The cert number, or '' when none can be derived.
 */
export function deriveCertNumber(tx) {
	if (!tx) return '';
	if (tx.certNumber) return String(tx.certNumber).trim();
	const fromNotes = extractCertificateNumber(tx.notes || '');
	if (fromNotes) return fromNotes;
	const fromDescription = extractCertificateNumber(tx.description || '');
	if (fromDescription) return fromDescription;
	if (isBareCertShape(tx.notes)) return tx.notes.trim();
	return '';
}

/**
 * Derives the canonical description for a transaction object across loader shapes.
 * Resolution order:
 *   1. Explicit `tx.description` when present (external CSV with a description column).
 *   2. `tx.notes` when it does NOT look like a bare cert string. Embedded clients
 *      (`src/shared_assets/index.js:3773` and friends) write description text into
 *      `tx.notes`; falling back here recovers their description data without
 *      reintroducing the Asplundh "cert as description" conflation.
 *
 * Returns '' when only cert-shaped data is available — honest empty for clients
 * whose source data has no description column.
 *
 * @param {Object} tx - Transaction object (any loader shape).
 * @returns {string} The description text, or '' when none can be derived.
 */
export function deriveDescription(tx) {
	if (!tx) return '';
	if (tx.description) return String(tx.description);
	if (typeof tx.notes !== 'string') return '';
	const trimmed = tx.notes.trim();
	if (!trimmed) return '';
	if (isBareCertShape(trimmed)) return '';
	return tx.notes;
}
